import * as React from "react";
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Textarea,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Settings as SettingsModel, saveSettings } from "../../zegal/settings";
import { ApiError, exchangeApiKey } from "../../zegal/api";

interface SettingsProps {
  settings: SettingsModel;
  onSave: (next: SettingsModel) => void;
  onCancel: () => void;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
  },
  actions: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    justifyContent: "flex-end",
    marginTop: tokens.spacingVerticalS,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  sectionLabel: {
    fontWeight: tokens.fontWeightSemibold,
    marginTop: tokens.spacingVerticalS,
  },
});

const SettingsPanel: React.FC<SettingsProps> = ({ settings, onSave, onCancel }) => {
  const styles = useStyles();
  const [apiUrl, setApiUrl] = React.useState(settings.apiUrl);
  const [apiVersion, setApiVersion] = React.useState(settings.apiVersion);
  const [apiKey, setApiKey] = React.useState(settings.apiKey);
  const [dragonball, setDragonball] = React.useState(settings.dragonball);
  const [username, setUsername] = React.useState(settings.username);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    const draft: SettingsModel = {
      apiUrl: apiUrl.trim() || "/zegal-api",
      apiVersion: apiVersion.trim(),
      apiKey: apiKey.trim(),
      dragonball: dragonball.trim(),
      username: username.trim(),
    };

    // If the user just set (or changed) an API key, exchange it for a
    // Dragonball token immediately so subsequent calls work.
    const apiKeyChanged = draft.apiKey && draft.apiKey !== settings.apiKey;
    if (apiKeyChanged) {
      try {
        const exchanged = await exchangeApiKey(draft, draft.apiKey);
        draft.dragonball = exchanged.dragonball;
        draft.username = exchanged.username || draft.username;
      } catch (e) {
        const msg = e instanceof ApiError ? `${e.status}: ${e.message}` : (e as Error).message;
        setError(`API key exchange failed: ${msg}`);
        setBusy(false);
        return;
      }
    }

    saveSettings(draft);
    setBusy(false);
    onSave(draft);
  };

  return (
    <div className={styles.root}>
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <Field label="API URL" hint="Relative '/zegal-api' uses the webpack dev proxy (recommended in dev).">
        <Input value={apiUrl} onChange={(_, d) => setApiUrl(d.value)} />
      </Field>
      <Field label="API Version" hint="Sent as X-Dragon-Law-API-Version.">
        <Input value={apiVersion} onChange={(_, d) => setApiVersion(d.value)} />
      </Field>

      <div className={styles.sectionLabel}>API Key (recommended)</div>
      <Field
        label="API Key"
        hint="Generate one in your Zegal account, then paste here. When saved, it's exchanged for a Dragonball via GET /api/auth/:key."
      >
        <Input
          value={apiKey}
          onChange={(_, d) => setApiKey(d.value)}
          placeholder="sk_... or pk_..."
        />
      </Field>

      <div className={styles.sectionLabel}>Manual credentials (advanced)</div>
      <Field label="Username" hint="Sent as X-Dragon-Law-Username.">
        <Input
          value={username}
          onChange={(_, d) => setUsername(d.value)}
          placeholder="you@zegal.com"
        />
      </Field>
      <Field label="Dragonball token" hint="Sent as X-Dragon-Law-Dragonball.">
        <Textarea
          value={dragonball}
          onChange={(_, d) => setDragonball(d.value)}
          rows={3}
          placeholder="Paste a Dragonball auth token"
        />
      </Field>

      <div className={styles.hint}>
        Auth resolution order: API Key (exchanged) &gt; manual Dragonball &gt; email/password login.
      </div>
      <div className={styles.actions}>
        <Button appearance="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button appearance="primary" onClick={handleSave} disabled={busy}>
          {busy ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default SettingsPanel;
